import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { FlowGuard } from '@/components/csv-offer/flow-guard';
import { InternalGuard } from '@/components/internal-guard';
// Eager: critical path (landing), auth flow, and DealPage (most-used /
// highest-value route — keeping it inline avoids a Suspense flash on the
// hot path).
import DealLayout from '@/pages/deal/deal-layout';
import { DealPage } from '@/pages/deal/page';
import HomePage from '@/pages/home';
import LoginPage from '@/pages/login';
import LogoutPage from '@/pages/logout';
import RedirectPage from '@/pages/redirect';
import { Loader2 } from 'lucide-react';

// Lazy: secondary routes loaded on navigation.
const SettingsPageLazy = lazy(() =>
  import('@/pages/deal/settings').then((m) => ({ default: m.SettingsPage }))
);
const HistoryPageLazy = lazy(() =>
  import('@/pages/deal/history').then((m) => ({ default: m.HistoryPage }))
);
const DealRedirect = lazy(() => import('@/pages/deal/deal-redirect'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));
const DealStartPageLazy = lazy(() =>
  import('@/pages/deal/start').then((m) => ({ default: m.DealStartPage }))
);
const CsvOfferLayoutLazy = lazy(() =>
  import('@/components/csv-offer/csv-offer-layout').then((m) => ({
    default: m.CsvOfferLayout,
  }))
);
const CsvFlowScreenLazy = lazy(() =>
  import('@/pages/deal/csv').then((m) => ({ default: m.CsvFlowScreen }))
);
const CsvCorrectionsPageLazy = lazy(() =>
  import('@/pages/deal/csv/corrections').then((m) => ({
    default: m.CsvCorrectionsPage,
  }))
);

function RouteFallback() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/redirect" element={<RedirectPage />} />

        {/* Deal routes: shared Layout mounted once per dealId (keeps the SSE
            deal-items stream alive while navigating between tabs), + legacy
            redirects. */}
        <Route path="/deal/:dealId" element={<DealLayout />}>
          <Route
            index
            element={
              <FlowGuard page="entry">
                <DealPage />
              </FlowGuard>
            }
          />
          <Route
            path="legacy"
            element={
              <FlowGuard page="legacy">
                <DealPage />
              </FlowGuard>
            }
          />
          <Route path="settings" element={<SettingsPageLazy />} />
          <Route
            path="start"
            element={
              <FlowGuard page="start">
                <DealStartPageLazy />
              </FlowGuard>
            }
          />
          <Route
            path="csv"
            element={
              <FlowGuard page="csv">
                <CsvOfferLayoutLazy />
              </FlowGuard>
            }
          >
            <Route index element={<CsvFlowScreenLazy />} />
            <Route
              path="corrections/:step"
              element={<CsvCorrectionsPageLazy />}
            />
          </Route>
          <Route
            path="history"
            element={
              <InternalGuard>
                <HistoryPageLazy />
              </InternalGuard>
            }
          />
        </Route>
        <Route path="/deal/:dealId/*" element={<DealRedirect />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
